import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, useEffect, useMemo, useState } from 'react';

import { DAY_LABELS } from '@/constants/dayOfWeek';
import { ELIGIBILITY_APPLICATION_KEY } from '@/constants/treatmentGroups';
import useJurisdictions from '@/hooks/useJurisdictions';
import { useLogicApplication } from '@/hooks/useLogicApplication';
import { useLogicGroups } from '@/hooks/useLogicGroups';
import {
  useMoveTreatmentGroupMembers,
  useTreatmentGroup,
  useTreatmentGroups
} from '@/hooks/useTreatmentGroups';
import { TreatmentGroup } from '@/types/treatmentGroup';
import { Alert, Input, Modal, RadioGroup, Spinner, Typography } from '@/ui';
import { formatTimeOfDay, todayLocalDateString } from '@/utils/dates';
import { getSelectedJurisdictionIds } from '@/utils/jurisdictionEligibility';

import type { Props } from './types';

const SEARCH_DEBOUNCE_MS = 300;

const isActiveMembership = (endOn: string | null) => !endOn || endOn >= todayLocalDateString();

const scheduleSummary = (group: Pick<TreatmentGroup, 'current_sessions'>) =>
  group.current_sessions
    .map(
      (session) =>
        `${DAY_LABELS[session.display_day_of_week]} ${formatTimeOfDay(session.display_start_time)}`
    )
    .join(', ') || 'No standing sessions';

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const MoveGroupMembersModal: FC<Props> = ({ sourceGroup, onClose, onSuccess }) => {
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [destinationGroupId, setDestinationGroupId] = useState('');

  // Reset every time a new group is opened for this action.
  useEffect(() => {
    setStep('select');
    setSearchInput('');
    setSearch('');
    setDestinationGroupId('');
  }, [sourceGroup?.id]);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Scope candidates to the source group's own jurisdiction(s), the same way
  // TreatmentGroupDetailSections resolves a group's jurisdiction names.
  const { data: eligibilityApplication, isPending: isPendingEligibilityApplication } =
    useLogicApplication(ELIGIBILITY_APPLICATION_KEY);
  const { data: rootGroups, isPending: isPendingRootGroups } = useLogicGroups(
    eligibilityApplication?.id,
    sourceGroup?.id
  );
  const { data: jurisdictions, isPending: isPendingJurisdictions } = useJurisdictions();
  // Until all three have loaded, an empty jurisdictionIds means "not
  // resolved yet", not "no restriction" -- gate the candidate query on it so
  // an unscoped result never gets a chance to render (and be selectable)
  // before the properly-scoped one replaces it.
  const isPendingJurisdictionScope =
    isPendingEligibilityApplication || isPendingRootGroups || isPendingJurisdictions;
  const jurisdictionIds = useMemo(
    () => getSelectedJurisdictionIds(rootGroups ?? [], jurisdictions ?? []),
    [rootGroups, jurisdictions]
  );

  const candidateParams = {
    ...(sourceGroup && { appointment_type: sourceGroup.appointment_type_id }),
    ...(jurisdictionIds.length > 0 && { jurisdiction: jurisdictionIds.join(',') }),
    ...(search && { search })
  };
  const { data: candidatesResponse, isPending: isPendingCandidates } = useTreatmentGroups(
    candidateParams,
    { enabled: !!sourceGroup && step === 'select' && !isPendingJurisdictionScope }
  );
  const candidates = (candidatesResponse?.data?.results ?? []).filter(
    (group) => group.id !== sourceGroup?.id && !group.is_draft && !group.end_date
  );

  // Both the list page's row and the destination-picker's candidate rows come
  // from the list endpoint, which deliberately omits `members` (see
  // TreatmentGroupListItemSerializer) -- so the `sourceGroup` prop can't be
  // trusted for its roster either. Re-fetch the full source group by id (a
  // no-op cache hit when it's already the detail view's own group, as when
  // this modal is opened from the group drawer) and use that everywhere a
  // roster is actually needed.
  const { data: sourceResponse, isPending: isPendingSource } = useTreatmentGroup(sourceGroup?.id);
  const sourceGroupDetail = sourceResponse?.data ?? null;

  // Likewise only safe to use for display -- once a candidate is chosen, its
  // full roster is fetched below and that's what actually gets merged into
  // the move payload.
  const { data: destinationResponse, isPending: isPendingDestination } = useTreatmentGroup(
    step === 'confirm' ? destinationGroupId : undefined
  );
  const destinationGroup = destinationResponse?.data ?? null;

  const activeSourceMembers = (sourceGroupDetail?.members ?? []).filter((member) =>
    isActiveMembership(member.end_on)
  );
  const copiedCount = destinationGroup
    ? activeSourceMembers.filter(
        (member) =>
          !destinationGroup.members.some((existing) => existing.person_id === member.person_id)
      ).length
    : activeSourceMembers.length;

  const moveMembers = useMoveTreatmentGroupMembers();

  const handleConfirm = () => {
    if (!sourceGroupDetail || !destinationGroup) return;
    moveMembers.mutate(
      { destinationGroup, sourceGroup: sourceGroupDetail },
      { onSuccess: ({ group, copiedCount: count }) => onSuccess(group, count) }
    );
  };

  if (!sourceGroup) return null;

  if (step === 'confirm') {
    const isPendingConfirm = isPendingDestination || isPendingSource;
    const nothingToCopy = copiedCount === 0;
    return (
      <Modal
        open
        onClose={onClose}
        title={nothingToCopy ? 'Nothing to add' : `Add ${pluralize(copiedCount, 'member')}?`}
        description="Step 2 of 2 · Confirm"
        size="lg"
        secondaryAction={{ label: 'Back', onClick: () => setStep('select') }}
        primaryAction={{
          label: `Add ${pluralize(copiedCount, 'member')}`,
          onClick: handleConfirm,
          isLoading: moveMembers.isPending,
          disabled: moveMembers.isPending || isPendingConfirm || !destinationGroup || nothingToCopy
        }}>
        {isPendingConfirm || !destinationGroup || !sourceGroupDetail ? (
          <Spinner label="Loading group details..." />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-lg border border-uniform-gray-200 p-3 dark:border-uniform-gray-700">
                <Typography variant="body" muted className="text-xs uppercase tracking-wide">
                  From
                </Typography>
                <Typography variant="body" className="font-semibold">
                  {sourceGroup.name}
                </Typography>
                <Typography variant="body" muted className="text-sm">
                  {scheduleSummary(sourceGroup)}
                </Typography>
              </div>
              <FontAwesomeIcon icon={faArrowRight} className="shrink-0 text-uniform-gray-400" />
              <div className="flex-1 rounded-lg border border-blue-400 bg-blue-50 p-3 dark:bg-blue-950/30">
                <Typography variant="body" muted className="text-xs uppercase tracking-wide">
                  To
                </Typography>
                <Typography variant="body" className="font-semibold">
                  {destinationGroup.name}
                </Typography>
                <Typography variant="body" muted className="text-sm">
                  {scheduleSummary(destinationGroup)}
                </Typography>
              </div>
            </div>

            {nothingToCopy ? (
              <Alert variant="info">
                Every active member of <span className="font-semibold">{sourceGroup.name}</span> is
                already in <span className="font-semibold">{destinationGroup.name}</span>.
                There&apos;s nothing to add. Go back to pick a different group, or close this
                dialog.
              </Alert>
            ) : (
              <>
                <div>
                  <Typography variant="body" className="font-semibold">
                    What will happen
                  </Typography>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li>
                      {pluralize(copiedCount, 'member')} added to{' '}
                      <span className="font-semibold">{destinationGroup.name}</span>
                    </li>
                    <li>
                      They&apos;re enrolled in all of its weekly sessions:{' '}
                      {scheduleSummary(destinationGroup)}
                    </li>
                    <li>
                      Nothing changes in this group. Members stay enrolled and the group keeps
                      running.
                    </li>
                  </ul>
                </div>

                <Alert variant="warning">
                  <span className="font-semibold">Everyone is enrolled in every session</span>, even
                  members who attend only some of this group&apos;s sessions. A scheduler can adjust
                  those members afterward.
                </Alert>

                <Typography variant="body" muted className="text-sm">
                  Anyone already in the destination group is skipped. You can remove members from
                  either group later.
                </Typography>
              </>
            )}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add members to another group"
      description="Step 1 of 2 · Choose a group"
      size="lg"
      secondaryAction={{ label: 'Cancel', onClick: onClose }}
      primaryAction={{
        label: 'Continue',
        onClick: () => setStep('confirm'),
        disabled: !destinationGroupId
      }}>
      <div className="space-y-4">
        {isPendingSource ? (
          <Spinner label="Loading group members..." />
        ) : (
          <Alert variant="info">
            The{' '}
            <span className="font-semibold">{pluralize(activeSourceMembers.length, 'member')}</span>{' '}
            of this group will be added to the group you choose and enrolled in all of its weekly
            sessions. They also stay in this group — nothing here is ended or removed.
          </Alert>
        )}

        <Input
          label="Destination group"
          placeholder="Search groups"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />

        {isPendingCandidates ? (
          <Spinner label="Loading groups..." />
        ) : candidates.length === 0 ? (
          <Typography variant="body" muted className="text-sm">
            No other groups found.
          </Typography>
        ) : (
          <div className="max-h-80 overflow-y-auto pr-1">
            <RadioGroup
              id="move-group-members-destination"
              selectedValue={destinationGroupId}
              onChange={(value) => setDestinationGroupId(String(value))}
              optionsClassName="flex-col items-stretch"
              options={candidates.map((group) => ({
                value: group.id,
                label: (
                  <div className="flex w-full items-center justify-between gap-3">
                    <div>
                      <Typography variant="body" className="font-semibold">
                        {group.name}
                      </Typography>
                      <Typography variant="body" muted className="text-sm">
                        {scheduleSummary(group)}
                      </Typography>
                    </div>
                    <Typography variant="body" muted className="shrink-0 text-sm">
                      {group.active_member_count} members
                    </Typography>
                  </div>
                )
              }))}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MoveGroupMembersModal;
